import {
  DataRoomFileUploadStatus,
  FileDownloadUrlQueryVariables,
  FileDownloadUrlQuery,
  FileType,
  FileDownloadUrlDocument,
  useTrackActivityMutation,
  DealActivityType,
} from "../graphql/generated";
import { ExternalLinkIcon, FileX } from "lucide-react";
import { FileIcon } from "./FileIcon";
import { PanelRightCloseIcon } from "lucide-react";
import { DataRoomFile } from "../graphql/generated";
import SyncfusionPdfViewer from "./document_viewers/SyncfusionPdfViewer";
import { SlideOver } from "./SlideOver";
import { Button } from "./tailwind/Button";
import useGqlClient from "../hooks/useGqlClient";
import {
  useDataRoomFileQuery,
  useFileVersionDownloadUrlQuery,
} from "../graphql/generated";
import Loading from "./Loading";
import { useEffect, useState } from "react";
import { AppState } from "../store";
import { dataRoomFileSelectors } from "../store/dataRoomFile/selector";
import { useSelector } from "react-redux";
import { Spinner } from "./icons/Spinner";
import { ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import { toasts } from "./toasts/toasts";
import { links } from "../utils/links";
import { useHistory, useParams } from "react-router-dom";
export interface Reference {
  file: DataRoomFile;
  fileVersionId: string;
  pageIndex: number;
  quote: string;
  rectsOnPage: Array<Array<number>>;
  folderId: string;
}

export function ReferenceSlideOver(props: {
  reference?: Reference;
  open: boolean;
  onClose: () => void;
}) {
  const client = useGqlClient();
  const history = useHistory();
  const { dealId: activeDealId } = useParams<{ dealId: string }>();
  const trackActivity = useTrackActivityMutation(client);

  return (
    <SlideOver open={props.open} onClose={props.onClose} maxWidth="max-w-3xl">
      <div className="h-full flex flex-col">
        <div className="border-b border-gray-200 shadow-sm p-4 flex justify-between space-x-2">
          <div className="flex items-center space-x-2">
            <PanelRightCloseIcon
              onClick={() => props.onClose()}
              className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-pointer"
            />
            <FileIcon
              fileType={props.reference?.file.fileType ?? FileType.Other}
            />
            <p className="text-sm text-gray-800 font-semibold">
              {props.reference?.file.name}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {props.reference && !props.reference.file.parentQuestion ? (
              <Button
                variant="neutral"
                size="s"
                text="Go to document"
                icon={ExternalLinkIcon}
                onClick={() => {
                  if (
                    !props.reference ||
                    !props.reference.file ||
                    !props.reference.file.folderId
                  ) {
                    return;
                  }
                  history.push(
                    links.fileLink(
                      props.reference.file.id,
                      props.reference.file.folderId,
                      activeDealId,
                    ),
                  );
                  props.onClose();
                }}
              />
            ) : null}
            <Button
              variant="neutral"
              size="s"
              icon={ArrowDownTrayIcon}
              text="Download"
              onClick={() => {
                if (!props.reference || !props.reference.file) {
                  return;
                }
                client
                  .request<FileDownloadUrlQuery, FileDownloadUrlQueryVariables>(
                    FileDownloadUrlDocument,
                    {
                      id: props.reference.file.id,
                    },
                  )
                  .then((res) => {
                    if (!props.reference) {
                      return;
                    }

                    const downloadUrl = res.fileDownloadUrl.downloadUrl;
                    const a = document.createElement("a");
                    a.href = downloadUrl;
                    a.target = "_blank";

                    document.body.appendChild(a);
                    a.click();

                    document.body.removeChild(a);

                    if (!props.reference.file.parentQuestion) {
                      trackActivity.mutate({
                        type: DealActivityType.FileDownloaded,
                        fileId: props.reference.file.id,
                        dataRoomId: "",
                        folderId: props.reference.file.folderId,
                      });
                    }
                  })
                  .catch((err) => {
                    toasts.error("Failed to download file");
                  });
              }}
            />
          </div>
        </div>
        <div className="flex-1 flex flex-col">
          {props.reference ? (
            <ReferenceFileView reference={props.reference} />
          ) : null}
        </div>
      </div>
    </SlideOver>
  );
}

function isSlideoverSupportedFileType(fileType: FileType): boolean {
  const supportedTypes = [
    FileType.Pdf,
    FileType.Docx,
    FileType.Doc,
    FileType.Pptx,
    FileType.Ppt,
    FileType.Xlsx,
    FileType.Xls,
    FileType.Qbb,
  ];
  return supportedTypes.includes(fileType);
}

function ReferenceFileView(props: { reference: Reference }) {
  const client = useGqlClient();
  const dataRoomFile = useDataRoomFileQuery(
    client,
    {
      id: props.reference.file.id,
    },
    {
      enabled: props.reference.file.id !== "",
    },
  );

  const version = dataRoomFile.data?.dataRoomFile.currentLiveVersion.id;
  props.reference.fileVersionId = version ?? "";

  const [url, setUrl] = useState<string>("");
  const fileDownloadUrl = useFileVersionDownloadUrlQuery(
    client,
    { id: version ?? "" },
    {
      enabled: Boolean(version),
      refetchOnWindowFocus(query) {
        if (query.isStaleByTime(1000 * 60 * 5)) {
          return true;
        }
        return false;
      },
    },
  );

  useEffect(() => {
    if (
      fileDownloadUrl.data &&
      fileDownloadUrl.data.fileVersionDownloadUrl.viewUrl !== url
    ) {
      setUrl(fileDownloadUrl.data.fileVersionDownloadUrl.viewUrl);
    }
  }, [
    fileDownloadUrl.data,
    fileDownloadUrl.isRefetching,
    fileDownloadUrl.isPending,
    url,
  ]);

  if (dataRoomFile.isLoading || !dataRoomFile.data) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (dataRoomFile.error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-gray-500/80">
          Error loading file. Please try again.
        </p>
      </div>
    );
  }

  if (!isSlideoverSupportedFileType(props.reference.file.fileType)) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 min-h-0">
        <div className="text-center max-w-sm">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileX className="w-6 h-6 text-gray-400" />
          </div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            Preview not available
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            This file format cannot be viewed in the preview. Use the download
            button above to access the file.
          </p>
        </div>
      </div>
    );
  }

  if (
    props.reference.file.fileType === FileType.Xlsx ||
    props.reference.file.fileType === FileType.Xls
  ) {
    return <ReferenceFileViewExcel reference={props.reference} />;
  }

  if (props.reference.file.fileType === FileType.Qbb) {
    return (
      <div className="h-[800px] flex flex-col items-center justify-center px-12">
        <FileIcon size="l" fileType={FileType.Qbb} />
        <p className="text-sm font-semibold mt-1 text-gray-700">
          This is a QuickBooks export
        </p>

        <p className="text-sm text-gray-500 text-center mt-2">
          You must download this document and view it inside the QuickBooks
          desktop application.
        </p>
      </div>
    );
  }

  if (fileDownloadUrl.error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-gray-500/80">
          Error loading file. Please try again.
        </p>
      </div>
    );
  }

  if (url === "") {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="h-full w-full min-h-0 overflow-hidden">
      <SyncfusionPdfViewer
        url={url}
        height="100%"
        pageIndex={props.reference.pageIndex}
        rectsOnPage={
          props.reference.rectsOnPage.length > 0
            ? props.reference.rectsOnPage
            : undefined
        }
      />
    </div>
  );
}

function ReferenceFileViewExcel(props: { reference: Reference }) {
  const client = useGqlClient();

  const dataRoomFile = useDataRoomFileQuery(
    client,
    {
      id: props.reference.file.id,
    },
    {
      enabled: props.reference.file.id !== "",
    },
  );

  const version = dataRoomFile.data?.dataRoomFile.currentLiveVersion.id;

  const [url, setUrl] = useState<string>("");
  const [fileDownloadError, setFileDownloadError] = useState<string>("");
  const selectedVersionId = useSelector((state: AppState) =>
    dataRoomFileSelectors.selectedFileVersionId(state, props.reference.file.id),
  );

  const fileDownloadUrl = useFileVersionDownloadUrlQuery(
    client,
    {
      id: version ?? "",
    },
    {
      refetchOnWindowFocus(query) {
        if (query.isStaleByTime(1000 * 60 * 5)) {
          return true;
        }
        return false;
      },
      enabled: version !== undefined && version !== "",
    },
  );
  useEffect(() => {
    if (
      fileDownloadUrl.data &&
      fileDownloadUrl.data.fileVersionDownloadUrl.viewUrl !== url
    ) {
      setFileDownloadError("");
      setUrl(fileDownloadUrl.data.fileVersionDownloadUrl.viewUrl);
      return;
    }
    if (fileDownloadUrl.error) {
      setFileDownloadError("Unable to load file");
      return;
    }
  }, [
    fileDownloadUrl.data,
    fileDownloadUrl.isRefetching,
    fileDownloadUrl.isPending,
    fileDownloadUrl.error,
    url,
  ]);
  if (
    props.reference.file.uploadStatus !== undefined &&
    props.reference.file.uploadStatus !== DataRoomFileUploadStatus.Ready &&
    props.reference.file.uploadStatus !== DataRoomFileUploadStatus.Uploaded
  ) {
    return (
      <div style={{ height: 800 }} className="flex items-center justify-center">
        <Spinner color="gray" size="s" />
        <p className="font-semibold text-gray-700 text-sm">Preparing file...</p>
      </div>
    );
  }
  if (url === "") {
    return (
      <div style={{ height: 800 }}>
        <Loading />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-75px)] w-full">
      <iframe
        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
          url,
        )}`}
        className="w-full h-full"
      />
    </div>
  );
}
